import type { MenuSection } from "api-types";

import { type MenuTreeNode, isValidGuestGroupHeader } from "./menu-tree.types";

export type MenuTreeBuildResult =
  | { kind: "legacy" }
  | { kind: "malformed" }
  | { kind: "tree"; roots: MenuTreeNode[] };

/**
 * The four fields this reads. Structural rather than `MenuSection` itself so a
 * caller can hand over a section whose dish list it has already narrowed, which
 * the generated class type cannot express. `MenuSection` satisfies it.
 */
export type GuestMenuSection = Pick<
  MenuSection,
  "id" | "dishesInDisplayOrder" | "headersById" | "itemsInDisplayOrder"
>;

const isNonEmptyString = (value: unknown): value is string =>
  typeof value === "string" && value.trim().length > 0;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null;

const isGuestHeader = (value: unknown): boolean =>
  isRecord(value) &&
  typeof value.title === "string" &&
  typeof value.required === "boolean";

/**
 * Builds the guest-only view of `itemsInDisplayOrder`.
 *
 * The response still carries the structural order and the public dishes as
 * separate collections. That means a missing or withheld reference is a data
 * problem, not an empty allergen list or a choice the guest can safely ignore.
 * We preserve it as an incomplete node so the renderer can fail closed.
 */
export const buildGuestMenuTree = (
  section: GuestMenuSection
): MenuTreeBuildResult => {
  const items = section.itemsInDisplayOrder;
  if (items === null || items === undefined) return { kind: "legacy" };
  if (!Array.isArray(items)) return { kind: "malformed" };

  const publicDishes = Array.isArray(section.dishesInDisplayOrder)
    ? section.dishesInDisplayOrder
    : [];
  const headersById = isRecord(section.headersById) ? section.headersById : {};
  const dishesById = new Map(publicDishes.map((dish) => [dish.id, dish]));
  const stack: MenuTreeNode[] = [];
  const roots: MenuTreeNode[] = [];

  for (const [index, item] of items.entries()) {
    if (
      !item ||
      !Number.isInteger(item.indent) ||
      item.indent < 0 ||
      item.indent > stack.length ||
      (item.kind === "dish" && !isNonEmptyString(item.dishId)) ||
      (item.kind === "header" && !isNonEmptyString(item.headerId)) ||
      (item.kind !== "dish" && item.kind !== "header")
    ) {
      return { kind: "malformed" };
    }

    const id = `${section.id}:${index}`;
    const node: MenuTreeNode =
      item.kind === "dish"
        ? {
            id,
            kind: "dish",
            depth: item.indent,
            dish: dishesById.get(item.dishId),
            sourceId: item.dishId,
            children: [],
          }
        : {
            id,
            kind: "header",
            depth: item.indent,
            header: isGuestHeader(headersById[item.headerId])
              ? section.headersById?.[item.headerId]
              : undefined,
            sourceId: item.headerId,
            children: [],
          };

    if (item.indent === 0) {
      roots.push(node);
    } else {
      const parent = stack[item.indent - 1];
      if (!parent) return { kind: "malformed" };
      parent.children.push(node);
    }

    stack[item.indent] = node;
    stack.length = item.indent + 1;
  }

  // A structured list cannot silently discard every visible dish. This is a
  // response mismatch rather than a legitimate empty menu section.
  if (roots.length === 0 && publicDishes.length > 0) {
    return { kind: "malformed" };
  }

  return { kind: "tree", roots: pruneEmptyGroups(roots) };
};

/**
 * Drops an optional choice group that has nothing under it, at any nesting
 * depth. Its owner is then assessed on its own merits: a skippable group has
 * no say in it.
 *
 * A required group is kept even with no children. Its options are unknown, not
 * absent, and dropping it would let the owner read as though it never had that
 * choice. A group with a broken header record is kept too: malformed data must
 * keep failing closed rather than disappear.
 */
const pruneEmptyGroups = (nodes: MenuTreeNode[]): MenuTreeNode[] => {
  const kept: MenuTreeNode[] = [];
  for (const node of nodes) {
    const children = pruneEmptyGroups(node.children);
    const isEmptyGroup =
      node.kind === "header" &&
      isValidGuestGroupHeader(node.header) &&
      !node.header.required &&
      children.length === 0;
    if (!isEmptyGroup) kept.push({ ...node, children });
  }
  return kept;
};
